// src/services/authApi.js
import { API_URL } from '../config/config.api';
import logger from '../utils/logger';

// A list of API paths that should NOT trigger an automatic token refresh on a 401 error.
// A 401 from these paths indicates a specific failure (e.g., wrong password)
// rather than an expired token.
const NO_REFRESH_PATHS = [
  '/api/auth/login-signup',
  '/api/auth/token', // Don't try to refresh if the refresh endpoint itself fails.
];

// Authenticated fetch wrapper
export const authFetch = async (url, options = {}) => {
  let config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include'
  };
  
  let response = await fetch(`${API_URL}${url}`, config);

  if (response.status === 401 && !NO_REFRESH_PATHS.includes(url)) {
    // The access token might be expired. Attempt to refresh it.
    // The browser will send the httpOnly refreshToken cookie automatically.
    try {
      const refreshResponse = await fetch(`${API_URL}/api/auth/token`, {
        method: 'POST',
        credentials: 'include',
      });

      if (refreshResponse.ok) {
        // Token refreshed successfully, retry the original request
        response = await fetch(`${API_URL}${url}`, config);
      } else {
        // Refresh failed. We will NOT call logout() here.
        // Instead, we return the original failed response. The caller (e.g., AuthContext)
        // is responsible for handling the UI state (like redirecting to login).
        // This prevents a failed session check from destroying a valid server session.
        return response;
      }
    } catch (refreshError) {
      logger.error('Token refresh error:', refreshError);
      // If the refresh mechanism itself throws an error (e.g., network issue),
      // we also return the original response and let the caller handle it.
      return response;
    }
  }

  return response;
};

// Convenience methods
export const authGet = (url, options = {}) => 
  authFetch(url, { ...options, method: 'GET' });

export const authPost = (url, data, options = {}) => 
  authFetch(url, { 
    ...options, 
    method: 'POST', 
    body: JSON.stringify(data) 
  });

export const authPut = (url, data, options = {}) => 
  authFetch(url, { 
    ...options, 
    method: 'PUT', 
    body: JSON.stringify(data) 
  });

export const authDelete = (url, options = {}) => 
  authFetch(url, { ...options, method: 'DELETE' });

// Check if user is authenticated
export const isAuthenticated = () => {
  // With HttpOnly cookies, we can't check for a token directly.
  // Instead, we check a session flag set upon successful login.
  return sessionStorage.getItem('isLoggedIn') === 'true';
};

// Logout function
export const logout = async () => {
  // Call the backend to clear the HttpOnly cookies
  await fetch(`${API_URL}/api/auth/logout`, { method: 'POST', credentials: 'include' });
  sessionStorage.removeItem('isLoggedIn');
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('rememberedEmail');
  // The redirect is now handled in AuthContext to ensure state is cleared first.
};